import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useMediaStream } from '../../components/conversations/hooks/useMediaStream';
import { usePreferences } from '../../hooks/usePreferences';
import { useForm, Controller } from 'react-hook-form';
import { getEmotions, getEthnicities, getGenders } from '../../lib/masterdata';
import VoiceCircle from '../../components/conversations/VoiceCircle';
import api from '../../api';
import { Ethnicity, Gender, toEthnicity, toEthnicityCode, toEthnicityText, toGender, toGenderCode, toGenderText } from '../../api/types/common';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import AppDrawer from '../../components/AppDrawer';
import { ScenariosSubLayout } from './ScenariosSubLayout';
import { ScenarioPrivilege } from '../../api/types/scenario';
import { ProgressIndicator } from '../../components/ProgressIndicator';
// @ts-ignore
import outputWorkletUrl from '../../components/conversations/worklets/output.js?url';
// @ts-ignore
import inputWorkletUrl from '../../components/conversations/worklets/input.js?url';
import { useNavigate } from 'react-router-dom';
import { SplashScreen } from './SplashScreen';
import { useSocketContext } from '../../sockets/context';

enum SCENARIO_EVENT {
  REQUEST_INITIAL_QUESTION = 'scenario:request_initial_question',
  REQUEST_FOLLOW_UP_QUESTION = 'scenario:request_follow_up_question',
  AUDIO_CHUNK = 'scenario:audio_chunk',
  AUDIO_FINAL = 'scenario:audio_final',
  SCENARIO_COMPLETE = 'scenario:scenario_complete',
  USER_AUDIO_CHUNK = 'scenario:user_audio_chunk',
  TRANSCRIPT = 'scenario:transcript'
};

type SkeletalScenario = Record<string, string | number | boolean> & {
  meta: Record<string, string>;
};

type UserPromptState = {
  micAvailable: boolean;
  turn: 'bot' | 'user';
  phase: 'intro' | 'generate' | 'review';
  scenario: SkeletalScenario | null;
  loading: boolean; 
  loadingMessage: string;
  projectedTimeMs: number;
  scenarioConversationId: string | null;
};

export const UserPrompt = () => {
  console.log('rendering UserPrompt');

  // Get user from Redux.
  const user = useSelector((state: RootState) => state.auth);
  const { register, handleSubmit, reset, control } = useForm();
  const navigate = useNavigate();

  const audioContextRef = useRef<AudioContext | null>(null);
  const outputWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const inputWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const outputAnalyserRef = useRef<AnalyserNode | null>(null);

  const [state, setState] = useState<UserPromptState>({
    micAvailable: false,
    turn: 'bot',
    phase: 'intro',
    scenario: null,
    loading: false,
    loadingMessage: '',
    projectedTimeMs: 0,
    scenarioConversationId: null,
  });

  const { getSocket } = useSocketContext();
  
  const socket = getSocket('', {
    auth: { accessToken: user.token, userUuid: user.profile?.uuid },
  });

  const { getPreference } = usePreferences();
  const audioPreferences = getPreference('audio');

  /**
   * Input stream management.
   */
  const constraints = useMemo<MediaStreamConstraints>(() => {
    return audioPreferences?.preferredInputDeviceId
      ? { audio: { deviceId: { exact: audioPreferences.preferredInputDeviceId } } }
      : { audio: true };
  }, [audioPreferences?.preferredInputDeviceId]);

  const mediaStream = useMediaStream(constraints);

  useEffect(() => {
    if (mediaStream?.stream) {
      setState((prev) => ({ ...prev, micAvailable: true }));

      if (state.turn === 'bot') {
        mediaStream.stream.getAudioTracks().forEach((track) => (track.enabled = false));
        if (inputWorkletNodeRef.current) {
          inputWorkletNodeRef.current.port.postMessage({ command: 'mute' });
        }
      } else {
        mediaStream.stream.getAudioTracks().forEach((track) => (track.enabled = true));
        if (inputWorkletNodeRef.current) {
          inputWorkletNodeRef.current.port.postMessage({ command: 'unmute' });
        }
      }
    }
  }, [mediaStream, state.turn]);

  useEffect(() => {
    if (!socket) return;
    const handleAudioChunk = (chunk: ArrayBuffer) => {
      if (outputWorkletNodeRef.current) {
        outputWorkletNodeRef.current.port.postMessage({ type: 'buffer', buffer: chunk });
      }
    };

    const handleTranscript = () => {
      setState((prev) => ({ ...prev, turn: 'bot' }));
    };

    // Listen for scenario events with the "scenario:" prefix.
    socket.on(SCENARIO_EVENT.SCENARIO_COMPLETE, handleScenarioComplete);
    socket.on(SCENARIO_EVENT.AUDIO_CHUNK, handleAudioChunk);
    socket.on(SCENARIO_EVENT.TRANSCRIPT, handleTranscript);

    return () => {
      socket.off(SCENARIO_EVENT.TRANSCRIPT, handleTranscript);
      socket.off(SCENARIO_EVENT.AUDIO_CHUNK, handleAudioChunk);
      socket.off(SCENARIO_EVENT.SCENARIO_COMPLETE, handleScenarioComplete);
    };
  }, [socket]);

  useEffect(() => {
    return () => {
      const audioContext = audioContextRef.current;
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close();
      }
    };
  }, []);

  useEffect(() => {
    if (!state.scenario) return;

    const { botEthnicity, botGender, botEmotion, ...rest } = state.scenario;
  
    reset({
      ...rest,
      botEthnicity: toEthnicity(botEthnicity as string), 
      botGender: toGender(botGender as string),
      botEmotion: 'Neutral',
    });
  }, [state.scenario, reset]);

  const handleScenarioComplete = async (scenario: SkeletalScenario) => {
    setState((prev) => ({ 
      ...prev, 
      loading: true, 
      loadingMessage: 'Generating your new scenario...', 
      projectedTimeMs: 2000,
      scenarioConversationId: scenario.meta.scenarioConversationId,
    }));

    setTimeout(() => {
      setState((prev) => ({ ...prev, phase: 'review', scenario, loading: false }));
    }, 2100);

    if (audioContextRef.current) {
      await audioContextRef.current.close();
    }
  };

  const handleStart = async () => {
    const audioContext = audioContextRef.current = new AudioContext({ sampleRate: 44100 });

    if (audioContext) {
      await audioContext.audioWorklet.addModule(outputWorkletUrl);
      outputWorkletNodeRef.current = new AudioWorkletNode(audioContext, 'output-processor');
      outputAnalyserRef.current = audioContext.createAnalyser();

      outputWorkletNodeRef.current
        .connect(outputAnalyserRef.current)
        .connect(audioContext.destination);

      outputWorkletNodeRef.current.port.onmessage = ({ data }) => {
        if (data.type === 'process' && data.finished) {
          setState((prev) => ({ ...prev, turn: 'user' }));
        }
      };

      await audioContext.audioWorklet.addModule(inputWorkletUrl);
      inputWorkletNodeRef.current = new AudioWorkletNode(audioContext, 'audio-input-processor');

      inputWorkletNodeRef.current.port.onmessage = (event) => {
        if (socket) {
          socket.emit(SCENARIO_EVENT.USER_AUDIO_CHUNK, event.data);
        }
      };

      if (mediaStream?.stream) {
        const mediaStreamSource = audioContext.createMediaStreamSource(mediaStream.stream);
        mediaStreamSource.connect(inputWorkletNodeRef.current);
      }

      if (socket) {
        socket.emit(SCENARIO_EVENT.REQUEST_INITIAL_QUESTION, {
          initialQuestion: `What's a challenging conversation at work we can help you practice?`
        });
      }

      setState((prev) => ({ ...prev, phase: 'generate' }));
    }
  };

  const onSubmit = async (data: any) => {
    const fakeCandidateDetails = {
      candidateGender: 'MALE',
      candidateEthnicity: 'BRITISH',
      candidateName: 'John Doe',
      candidateAge: 30,
      candidateTenureYears: 10,
      candidateJobTitle: 'Software Engineer',
      candidateEducation: 'Bachelors'
    };

    const keys = ['botGender', 'botEthnicity', 'botEmotion', 'botPersonality'];
    Object.keys(data).forEach((key) => {
      if (keys.includes(key)) {
        data[key] = data[key].toUpperCase();
      }
    });
    data.botAge = parseInt(data.botAge);

    const { meta, ...dataWithoutMeta } = data;

    const body = {
      scoringMetrics: [
        'Active Listening',
        'Clarity'
      ],
      ...dataWithoutMeta,
      ...fakeCandidateDetails
    };

    try {
      setState((prev) => ({ 
        ...prev, 
        loading: true, 
        loadingMessage: 'Taking you to your new scenario...', 
        projectedTimeMs: 15000
      }));
  
      const generatedScenario = await api.generateScenario(body);
      const character = await api.inferCharacter({
        gender: data.botGender,
        age: data.botAge,
        ethnicity: data.botEthnicity
      });
      const res = await api.createScenario({
        ...generatedScenario,

        // @ts-ignore: I diudn't want to change the API right now.
        scenarioConversationId: state.scenarioConversationId,
        
        botAvatar: character.thumbnail,
        botVoice: character.voice,
        group: 'U',
        privilege: ScenarioPrivilege.USER
      });
      navigate(`/scenarios/${res.uuid}`);
    } catch (err: unknown) {
      console.log(err);
    }
  };

  return (
    <>
      {state.loading && (
        <ProgressIndicator projectedTimeMs={state.projectedTimeMs} intervalMs={500} isComplete={!state.loading} message={state.loadingMessage} />
      )}
      {mediaStream?.error && (
        <Alert severity="error">Microphone Unavailable</Alert>
      )}
      {state.phase === 'intro' && (
        <SplashScreen onStart={handleStart} />
      )}
      {state.phase === 'generate' && (
        <Box>
          <Typography variant="h5" fontWeight="normal">
            Let's start with some questions
          </Typography>
          <Typography variant="body1">
            Tell me about some of the challenges you face at work
          </Typography>
          <Box>
            <VoiceCircle analyzer={outputAnalyserRef.current} />
          </Box>
        </Box>
      )}
      {state.phase === 'review' && state.scenario && (
        <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ textAlign: 'left' }}>
          <Grid container spacing={3}>
            <Grid item sm={12}>
              <Typography variant="h5" fontWeight="normal">
                Let's review your new scenario
              </Typography>
            </Grid>
            <Grid item sm={12}>
              <Typography variant="body1">
                Scenario: {state.scenario?.name}
              </Typography>
            </Grid>
            <Grid item sm={12}>
              <InputLabel htmlFor="objective">Objective:</InputLabel>
              <TextField fullWidth {...register("objective")} multiline minRows={4} maxRows={4} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="botName">Name:</InputLabel>
              <TextField {...register("botName")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="botAge">Age:</InputLabel>
              <TextField {...register("botAge")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="gender">Gender:</InputLabel>
              <Controller
                name="botGender"
                control={control}
                defaultValue={Gender.UNSPECIFIED}
                render={({ field }) => (
                  <Select {...field} fullWidth required>
                    {getGenders().map((g: Gender) => (
                      <MenuItem key={toGenderCode(g)} value={toGender(g)}>{toGenderText(g)}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor="botEthnicity">Ethnicity</InputLabel>
              <Controller
                name="botEthnicity"
                control={control}
                defaultValue={toEthnicity(state.scenario.botEthnicity as string)}
                render={({ field }) => (
                  <Select {...field} fullWidth required>
                    {getEthnicities().map((e: Ethnicity) => (
                      <MenuItem key={toEthnicityCode(e)} value={toEthnicity(e)}>{toEthnicityText(e)}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Grid>
            <Grid item sm={12}>
              <InputLabel htmlFor="botPersonality">Personality:</InputLabel>
              <TextField fullWidth {...register("botPersonality")} />
            </Grid>
            <Grid item sm={12}>
              <InputLabel htmlFor="botEmotion">Emotion:</InputLabel>
              <Controller
                name="botEmotion"
                control={control}
                defaultValue={state?.scenario?.botEmotion || 'Neutral'}
                render={({ field }) => (
                  <Select {...field} fullWidth required>
                    {getEmotions().map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Grid>
            <Grid item sm={12}>
              <Button type="submit" fullWidth variant="contained" sx={{ height: '48px' }}>
                Save Scenario
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default () => (
  <AppDrawer
    mainContent={
      <ScenariosSubLayout>
        <UserPrompt />
      </ScenariosSubLayout>
    }
    pageName="Get Started"
  />
);
